// Cents -> compact USD display, shared by the live estimate panel
// (EstimatePanel.jsx, pre-capture — no worthCents) and the post-capture
// breakdown reveal (BreakdownStack.jsx — worthCents present). Rounds to the
// nearest whole dollar; the engine already rounds to the cent and no
// calculator copy shows fractional dollars.

export function formatCents(cents: number): string {
  const dollars = Math.round((Number.isFinite(cents) ? cents : 0) / 100)
  return `$${dollars.toLocaleString('en-US')}`
}

export function formatCentsRange(minCents: number, maxCents: number): string {
  if (minCents === maxCents) return formatCents(minCents)
  return `${formatCents(minCents)}–${formatCents(maxCents)}`
}

// Shared breakdown-rendering rules (review fix, Minor 5) — used by both the
// post-capture BreakdownStack.jsx panel and the estimate email
// (src/lib/calculator/notify.js) so the two surfaces stay in lockstep.

type BreakdownLikeItem = {
  label: string
  minCents: number
  maxCents: number
  breakdownLabel?: string | null
  multiplierPct?: number | null
}

/**
 * Declutters "nothing extra to build here" answers: a zero-cost item (no
 * addMinCents/addMaxCents) with no explicit breakdownLabel on its option
 * conveys nothing worth a line — e.g. "Branding: fully built out" or
 * "Google profile: claimed and active" cost $0 and would otherwise render
 * as a bare "$0" row. An item WITH an explicit breakdownLabel (e.g. the
 * timeline options) still renders even at $0, because its author meant it
 * to be seen.
 */
export function visibleBreakdownItems<T extends BreakdownLikeItem>(items: T[]): T[] {
  return items.filter((item) => !(item.minCents === 0 && item.maxCents === 0 && !item.breakdownLabel))
}

// Trims a trailing " (+30%)"-style parenthetical off a label before folding
// it into the multiplier summary line, so "Accelerated timeline (+30%)"
// reads as "Accelerated timeline — ×1.3 applied to total" instead of
// repeating the percentage twice in different notations.
function stripTrailingParenthetical(label: string): string {
  return label.replace(/\s*\([^)]*\)\s*$/, '').trim()
}

function formatMultiplier(appliedMultiplierPct: number): string {
  return (appliedMultiplierPct / 100).toFixed(2).replace(/\.?0+$/, '')
}

/**
 * One summary line describing the composed timeline/rush multiplier's net
 * effect on the total (e.g. "Accelerated timeline — ×1.3 applied to
 * total"), or null when appliedMultiplierPct is 100 (no multiplier in
 * effect — nothing to report). Named after whichever selected option(s)
 * actually carried a non-100 multiplierPct, so it stays correct even if a
 * future config composes more than one multiplier-bearing question.
 */
export function multiplierSummaryLine<T extends BreakdownLikeItem>(
  items: T[],
  appliedMultiplierPct: number,
): string | null {
  if (appliedMultiplierPct === 100) return null
  const contributors = [
    ...new Set(
      items
        .filter((item) => typeof item.multiplierPct === 'number' && item.multiplierPct !== 100)
        .map((item) => stripTrailingParenthetical(item.breakdownLabel || item.label || ''))
        .filter(Boolean),
    ),
  ]
  const label = contributors.length ? contributors.join(', ') : 'Timeline'
  return `${label} — ×${formatMultiplier(appliedMultiplierPct)} applied to total`
}
